<script setup lang="ts">
import type { SolrNewestDocsParams } from '~/composables/useNewestDocsQuery'
import { SolrParamsKey } from '~~/shared/solr'

const MAX_SLIDES_TO_SCROLL = 4
const CAROUSEL_PAGES = 3
const MAX_PUBLICATIONS = MAX_SLIDES_TO_SCROLL * CAROUSEL_PAGES

const selectedTab = ref<typeof PUBLICATION_ITEM_OPTIONS[number]>(PUBLICATION_ITEM_OPTIONS[0])

const queryParams = computed<SolrNewestDocsParams>(() => ({
  [SolrParamsKey.Newest]: {
    rows: MAX_PUBLICATIONS,
    start: 0,
    ...(selectedTab.value && { contentType: selectedTab.value.value }),
  },
}))

const { data, isLoading, isError } = useNewestDocsQuery(queryParams)

const allPublications = computed(() => data.value?.response?.docs ?? [])

const { width } = useWindowSize()

const slidesToScroll = computed(() => {
  if (width.value < 768) {
    return 1
  }
  if (width.value < 1024) {
    return 2
  }
  if (width.value < 1440) {
    return 3
  }
  return MAX_SLIDES_TO_SCROLL
})

const isMounted = useMounted()

const publications = computed(() => isMounted.value ? allPublications.value.slice(0, slidesToScroll.value * CAROUSEL_PAGES) : allPublications.value)
</script>

<template>
  <div class="flex flex-col gap-m w-full max-w-[1380px] pr-0 py-m 2xl:pr-m">
    <div class="flex items-center gap-m max-md:-mr-m">
      <Tabs v-model="selectedTab" :options="PUBLICATION_ITEM_OPTIONS" class="flex min-w-0 max-md:[&_[role=tablist]]:pr-m" />
    </div>

    <Carousel v-if="isLoading" aria-label="Nyutgivelser laster" :slides-to-scroll="slidesToScroll" variant="default" controls class="max-md:-mr-m">
      <div v-for="n in MAX_PUBLICATIONS" :key="`skeleton-${n}`">
        <AcademicArticleCardSkeleton v-if="selectedTab?.value === 'fagartikler'" />
        <ContentCardSkeleton v-else />
      </div>
    </Carousel>

    <div v-else-if="isError" class="flex items-center justify-center py-xl">
      <div role="alert" class="text-error">
        Kunne ikke laste publikasjoner
      </div>
    </div>

    <Carousel v-else-if="publications.length > 0" :key="selectedTab.value" aria-label="Nyutgivelser" :slides-to-scroll="slidesToScroll" variant="default" controls dots class="max-md:-mr-m [--carousel-dot:theme(colors.carbon.100)]">
      <template v-for="doc in publications" :key="doc.id">
        <AcademicArticleCard
          v-if="doc.nodeType === 'academic_article'"
          :doc="doc"
          size="small"
          class="w-[317px] max-md:last:mr-m"
        />
        <ContentCard v-else :item="doc" size="small" class="max-md:last:mr-m" />
      </template>
    </Carousel>

    <div v-else class="flex items-center justify-center py-xl">
      <span class="text-secondary">Ingen publikasjoner funnet</span>
    </div>
  </div>
</template>
